import { useRef, useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import {
  Shield, Coins, ArrowRight, BookOpen, Flame, Target, Crown,
  Timer, TrendingUp, Zap, Lock, Gamepad2, Eye, EyeOff, Skull, Scroll, Compass, ChevronRight
} from 'lucide-react';

// ───────── Chapter definitions ─────────
const CHAPTERS = [
  {
    id: 'problem',
    num: 'I',
    icon: Skull,
    title: 'The Problem',
    sub: 'Why most meme coins rot',
    color: '#ef4444',
  },
  {
    id: 'seasons',
    num: 'II',
    icon: Timer,
    title: 'The Seasons',
    sub: 'A 30 day war, every month',
    color: '#22c55e',
  },
  {
    id: 'currencies',
    num: 'III',
    icon: Coins,
    title: 'Two Currencies',
    sub: 'Free layer. Token layer. Never mixed.',
    color: '#a855f7',
  },
  {
    id: 'phases',
    num: 'IV',
    icon: TrendingUp,
    title: 'The Pressure Gauge',
    sub: 'Five phases of every season',
    color: '#22c55e',
  },
  {
    id: 'skill',
    num: 'V',
    icon: Shield,
    title: 'Skill Over Spending',
    sub: 'Whales cannot buy victory',
    color: '#a855f7',
  },
  {
    id: 'chain',
    num: 'VI',
    icon: Flame,
    title: 'The Chain',
    sub: 'Daily streaks. Consistency wins.',
    color: '#ff6b00',
  },
  {
    id: 'escalation',
    num: 'VII',
    icon: Target,
    title: 'Failure Escalation',
    sub: 'Every miss makes the next one bigger',
    color: '#ef4444',
  },
  {
    id: 'fog',
    num: 'VIII',
    icon: EyeOff,
    title: 'Fog of War',
    sub: 'Controlled uncertainty creates tension',
    color: '#06b6d4',
  },
  {
    id: 'legends',
    num: 'IX',
    icon: Crown,
    title: 'Sewer Legends',
    sub: 'Cross season reputation',
    color: '#ffd700',
  },
  {
    id: 'fair-launch',
    num: 'X',
    icon: Flame,
    title: 'The Fair Launch',
    sub: 'Pump.fun · no allocation · SOL back to winners',
    color: '#eab308',
  },
  {
    id: 'compliance',
    num: 'XI',
    icon: Lock,
    title: 'Skill Not Gambling',
    sub: 'No bets. No stakes. No jackpots.',
    color: '#06b6d4',
  },
  {
    id: 'truth',
    num: 'XII',
    icon: Scroll,
    title: 'The Honest Truth',
    sub: 'What we promise. What we do not.',
    color: '#a855f7',
  },
];

// ───────── Reveal wrapper ─────────
const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}>
      {children}
    </motion.div>
  );
};

// ───────── Sigil component (chapter icon with glow) ─────────
const Sigil = ({ icon: Icon, color, size = 48, active = false }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <motion.div className="absolute pointer-events-none rounded-full"
      style={{
        inset: '-8px',
        background: `radial-gradient(circle, ${color}40 0%, transparent 65%)`,
        filter: 'blur(10px)',
      }}
      animate={active ? { opacity: [0.6, 1, 0.6] } : { opacity: 0.4 }}
      transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
    />
    <div className="relative w-full h-full rounded-full flex items-center justify-center"
      style={{
        background: `radial-gradient(circle at 40% 35%, ${color}30 0%, rgba(10,5,15,0.95) 75%)`,
        border: `1px solid ${color}${active ? '80' : '35'}`,
        boxShadow: active ? `0 0 18px ${color}50` : 'none',
        transition: 'all 0.3s ease',
      }}>
      <Icon style={{ width: size * 0.42, height: size * 0.42, color, filter: `drop-shadow(0 0 6px ${color}80)` }} />
    </div>
  </div>
);

// ───────── Chapter content blocks ─────────
const ChapterContent = ({ chapterId, navigate }) => {
  if (chapterId === 'problem') return (
    <div className="space-y-3 text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
      <p>Ninety five percent of meme coins follow the same script. Launch, pump for 48 hours, insiders dump, community left with worthless bags. No utility. No reason to come back after day one.</p>
      <p>On top of that, Solana users lose real money every day through failed transactions that still charge fees. Millions disappear into the void, and nobody talks about it.</p>
      <p className="pt-2" style={{ color: 'rgba(255,215,150,0.85)' }}>$GFG was built to fix both.</p>
    </div>
  );

  if (chapterId === 'seasons') return (
    <div className="space-y-4">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
        Instead of one hype moment at launch that fades forever, $GFG runs on 30 day cycles called <strong style={{ color: '#ffd700' }}>Seasons</strong>. Each has a community burn target, a shared reward pool, and a leaderboard. Every month is a fresh start.
      </p>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {[
          { t: 'Free Arcade Games', d: 'Four skill games. No $GFG required to play.', c: '#a855f7' },
          { t: 'Voluntary Burns', d: 'Burn Portal destroys supply for bonus Season Points.', c: '#ff6b00' },
          { t: 'Community Rewards', d: 'Hit the burn target and rewards distribute to all SP holders.', c: '#ffd700' },
          { t: 'Season Tithe', d: 'Team burns from a dedicated reserve every 30 days.', c: '#a855f7' },
          { t: 'Rollover on Failure', d: 'Target missed? Rewards carry forward with multipliers.', c: '#22c55e' },
        ].map(x => (
          <div key={x.t} className="p-3" style={{ background: `${x.c}08`, border: `1px solid ${x.c}20` }}>
            <div className="w-1.5 h-1.5 rounded-full mb-1.5" style={{ background: x.c, boxShadow: `0 0 4px ${x.c}` }} />
            <h5 className="text-[12px] font-bold mb-1" style={{ color: 'rgba(240,230,255,0.85)', fontFamily: "'Cinzel', serif" }}>{x.t}</h5>
            <p className="text-[11px]" style={{ color: 'rgba(220,210,240,0.55)' }}>{x.d}</p>
          </div>
        ))}
      </div>
    </div>
  );

  if (chapterId === 'currencies') return (
    <div className="space-y-4">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
        The economy splits into two layers that never mix. Games stay accessible. Tokens keep real value.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="p-4" style={{ background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.2)' }}>
          <p className="text-[9px] tracking-[0.25em] uppercase font-bold mb-2" style={{ color: '#a855f7', fontFamily: "'Courier New', monospace" }}>Free Layer</p>
          <h4 className="text-sm font-bold mb-2" style={{ color: 'rgba(240,230,255,0.9)', fontFamily: "'Cinzel', serif" }}>In Game Fees</h4>
          <ul className="text-[11.5px] space-y-1" style={{ color: 'rgba(220,210,240,0.6)' }}>
            <li>• Earned by playing arcade games</li>
            <li>• Used to replay or unlock content</li>
            <li>• Zero monetary value</li>
            <li>• Never converts to $GFG</li>
          </ul>
        </div>
        <div className="p-4" style={{ background: 'rgba(255,107,0,0.05)', border: '1px solid rgba(255,107,0,0.2)' }}>
          <p className="text-[9px] tracking-[0.25em] uppercase font-bold mb-2" style={{ color: '#ff6b00', fontFamily: "'Courier New', monospace" }}>Token Layer</p>
          <h4 className="text-sm font-bold mb-2" style={{ color: 'rgba(240,230,255,0.9)', fontFamily: "'Cinzel', serif" }}>$GFG Tokens</h4>
          <ul className="text-[11.5px] space-y-1" style={{ color: 'rgba(220,210,240,0.6)' }}>
            <li>• Burned voluntarily for bonus SP</li>
            <li>• Holding determines tier multipliers</li>
            <li>• Community rewards paid in $GFG</li>
            <li>• Traded freely on Solana DEX</li>
          </ul>
        </div>
      </div>
      <p className="text-[11px] pt-1" style={{ color: 'rgba(255,200,150,0.5)', fontStyle: 'italic' }}>
        No $GFG enters or exits any game. Burns are always voluntary and one way.
      </p>
    </div>
  );

  if (chapterId === 'phases') return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>Every season arcs through five phases that create natural tension and strategic moments.</p>
      {[
        { p: 'Ignition', d: '1-5', m: '1.2x', c: '#22c55e', x: 'Early bird bonus. Burns earn 20% more SP. Rewards commitment.' },
        { p: 'Calibration', d: '6-10', m: '1.0x', c: '#3b82f6', x: 'Target auto adjusts based on Ignition participation. Locks day 10.' },
        { p: 'The Grind', d: '11-25', m: '1.0x', c: '#6b7280', x: 'Standard rates. Daily streaks separate the serious from the casual.' },
        { p: 'Fever', d: '26-28', m: '1.15x', c: '#f59e0b', x: 'If progress is above 80%, everyone gets retroactive +15% SP.' },
        { p: 'The Wire', d: '29-30', m: '1.5x', c: '#ef4444', x: 'Final 48 hours. Burns earn 1.5x when progress is 85-99%. Peak drama.' },
      ].map(phase => (
        <div key={phase.p} className="flex items-start gap-3 p-3" style={{ background: 'rgba(10,5,15,0.4)', border: `1px solid ${phase.c}20` }}>
          <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ background: phase.c, boxShadow: `0 0 6px ${phase.c}` }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[13px] font-bold" style={{ color: phase.c, fontFamily: "'Cinzel', serif" }}>{phase.p}</span>
              <span className="text-[9px] font-mono px-1.5 py-0.5" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)' }}>DAYS {phase.d}</span>
              {phase.m !== '1.0x' && (
                <span className="text-[9px] font-mono font-bold px-1.5 py-0.5" style={{ background: `${phase.c}18`, color: phase.c }}>{phase.m} BURNS</span>
              )}
            </div>
            <p className="text-[11.5px]" style={{ color: 'rgba(220,210,240,0.55)' }}>{phase.x}</p>
          </div>
        </div>
      ))}
    </div>
  );

  if (chapterId === 'skill') return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>Three mechanisms prevent whales from dominating through pure spending.</p>
      <div className="p-4" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.18)' }}>
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4" style={{ color: '#a855f7' }} />
          <h5 className="text-[13px] font-bold" style={{ color: 'rgba(240,230,255,0.9)', fontFamily: "'Cinzel', serif" }}>Logarithmic Burn Curve</h5>
        </div>
        <p className="text-[11.5px] mb-3" style={{ color: 'rgba(220,210,240,0.6)' }}>Burn SP scales logarithmically. Small burns feel impactful. Massive burns hit diminishing returns hard.</p>
        <div className="grid grid-cols-4 gap-2">
          {[['10K','345'],['50K','580'],['100K','830'],['1M','997']].map(([b, s]) => (
            <div key={b} className="text-center p-2" style={{ background: 'rgba(168,85,247,0.08)' }}>
              <p className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>{b} burned</p>
              <p className="text-[13px] font-mono font-bold" style={{ color: '#a855f7' }}>{s} SP</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>Bonuses: Pyre +15%, Inferno +25% tiers.</p>
      </div>
      <div className="p-4" style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)' }}>
        <div className="flex items-center gap-2 mb-1.5"><Gamepad2 className="w-4 h-4" style={{ color: '#22c55e' }} /><h5 className="text-[13px] font-bold" style={{ color: 'rgba(240,230,255,0.9)', fontFamily: "'Cinzel', serif" }}>Skill Floor (3x Cap)</h5></div>
        <p className="text-[11.5px]" style={{ color: 'rgba(220,210,240,0.6)' }}>Burn SP is capped at 3x your arcade SP. Zero arcade SP means zero burn SP. You must play.</p>
      </div>
      <div className="p-4" style={{ background: 'rgba(255,107,0,0.05)', border: '1px solid rgba(255,107,0,0.15)' }}>
        <div className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4" style={{ color: '#ff6b00' }} /><h5 className="text-[13px] font-bold" style={{ color: 'rgba(240,230,255,0.9)', fontFamily: "'Cinzel', serif" }}>Tier Multiplier Cap (1.5x)</h5></div>
        <p className="text-[11.5px]" style={{ color: 'rgba(220,210,240,0.6)' }}>Holder tiers apply up to 2.5x for arcade SP but max out at 1.5x on burns. Holding helps. Skill wins.</p>
      </div>
    </div>
  );

  if (chapterId === 'chain') return (
    <div className="space-y-4">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>Consistency is rewarded. Play at least one game per day to build your streak.</p>
      <div className="grid grid-cols-3 gap-3">
        {[['+1%', 'per day'], ['+30%', 'max at day 30'], ['1 day', 'grace period']].map(([v, l]) => (
          <div key={l} className="text-center p-3" style={{ background: 'rgba(255,107,0,0.05)', border: '1px solid rgba(255,107,0,0.15)' }}>
            <p className="text-lg font-black font-mono" style={{ color: '#ff6b00' }}>{v}</p>
            <p className="text-[9px] font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>{l}</p>
          </div>
        ))}
      </div>
      <p className="text-[11.5px]" style={{ color: 'rgba(220,210,240,0.6)' }}>Miss one day and your streak freezes. Miss two and it resets. The streak bonus applies to all arcade SP, which makes regular players far more competitive than sporadic whales.</p>
    </div>
  );

  if (chapterId === 'escalation') return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>When the community misses the burn target, the next season intensifies. Rewards roll forward with multipliers.</p>
      {[
        ['Calm', '1.0x', '0%', '#22c55e', 'Normal season. Standard target and rewards.'],
        ['Tension', '1.5x', '10%', '#f59e0b', '1 failure. Reward pool grows 1.5x. Target reduced 10%.'],
        ['Crisis', '2.0x', '15%', '#ef4444', '2 failures. Reward pool doubles. Exclusive cosmetic unlocks.'],
        ['Meltdown', '3.0x', '25%', '#dc2626', '3+ failures. Reward pool triples. The sewers go red.'],
      ].map(([lvl, mult, red, c, desc]) => (
        <div key={lvl} className="flex items-center gap-3 p-2.5" style={{ background: 'rgba(10,5,15,0.4)', border: `1px solid ${c}20` }}>
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c, boxShadow: `0 0 6px ${c}` }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-[12px] font-bold" style={{ color: c, fontFamily: "'Cinzel', serif" }}>{lvl}</span>
              <span className="text-[9px] font-mono px-1.5 py-0.5" style={{ background: `${c}18`, color: c }}>{mult} REWARDS</span>
              <span className="text-[9px] font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>-{red} TARGET</span>
            </div>
            <p className="text-[11px]" style={{ color: 'rgba(220,210,240,0.55)' }}>{desc}</p>
          </div>
        </div>
      ))}
      <p className="text-[11px] pt-1 italic" style={{ color: 'rgba(255,200,150,0.5)' }}>Hit a target and escalation resets. The longer the drought, the bigger the eventual payout.</p>
    </div>
  );

  if (chapterId === 'fog') return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>Full transparency kills tension. Controlled uncertainty creates better decisions and more dramatic seasons.</p>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {[
          { i: EyeOff, c: '#06b6d4', t: 'Hidden SP', d: 'Others\' exact Season Points are hidden. Only rank and tier visible.' },
          { i: Timer, c: '#06b6d4', t: '6 Hour Refresh', d: 'Leaderboard updates every 6 hours. Creates anticipation.' },
          { i: Lock, c: '#ef4444', t: 'Final Day Blackout', d: 'Last 24 hours, the board freezes. Results revealed at season end.' },
          { i: Eye, c: '#22c55e', t: 'Your SP Visible', d: 'You always see your own SP and full breakdown. Only others are obscured.' },
        ].map(x => (
          <div key={x.t} className="p-3 flex items-start gap-2.5" style={{ background: 'rgba(10,5,15,0.4)', border: `1px solid ${x.c}15` }}>
            <x.i className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: x.c }} />
            <div>
              <h5 className="text-[12px] font-bold mb-0.5" style={{ color: 'rgba(240,230,255,0.85)', fontFamily: "'Cinzel', serif" }}>{x.t}</h5>
              <p className="text-[11px]" style={{ color: 'rgba(220,210,240,0.55)' }}>{x.d}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (chapterId === 'legends') return (
    <div className="space-y-4">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>One good season does not define you. Reputation builds over time.</p>
      <div>
        <h5 className="text-[11px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: 'rgba(255,215,0,0.6)', fontFamily: "'Courier New', monospace" }}>Seasonal Ranks</h5>
        <div className="flex flex-wrap gap-2">
          {[
            ['Mythic', 'Top 1%', '#ff00ff'], ['Diamond', 'Top 5%', '#00bfff'], ['Platinum', 'Top 10%', '#00ffff'],
            ['Gold', 'Top 25%', '#ffd700'], ['Silver', 'Top 50%', '#c0c0c0'], ['Bronze', '>50%', '#cd7f32'],
          ].map(([n, p, c]) => (
            <div key={n} className="px-3 py-2 text-center" style={{ background: `${c}08`, border: `1px solid ${c}25` }}>
              <p className="text-[10px] font-mono font-bold" style={{ color: c, textShadow: `0 0 4px ${c}40` }}>{n}</p>
              <p className="text-[8px] font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>{p}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h5 className="text-[11px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: 'rgba(255,215,0,0.6)', fontFamily: "'Courier New', monospace" }}>Persistent Reputation Score</h5>
        <p className="text-[11.5px]" style={{ color: 'rgba(220,210,240,0.6)' }}>PRS aggregates rank points across all seasons, weighted by recency. A 3 season Diamond who fell to Gold still ranks above a one time Diamond. Consistency wins.</p>
      </div>
      <div>
        <h5 className="text-[11px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: 'rgba(255,215,0,0.6)', fontFamily: "'Courier New', monospace" }}>Permanent Titles</h5>
        <div className="flex flex-wrap gap-2">
          {[
            ['First Blood', 'S1 participant', '#ff4400'],
            ['Iron Will', '5 consecutive seasons', '#c0c0c0'],
            ['Ghost', 'Top 10% with zero burns', '#a855f7'],
            ['Comeback King', 'Bronze to Diamond', '#00bfff'],
            ['Crisis Veteran', 'Survived 3+ failures', '#ef4444'],
          ].map(([t, d, c]) => (
            <span key={t} title={d} className="px-2.5 py-1.5 text-[10px] font-mono font-bold"
              style={{ background: `${c}12`, color: c, border: `1px solid ${c}25` }}>
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  if (chapterId === 'fair-launch') return (
    <div className="space-y-4">
      <div className="p-5 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(234,179,8,0.08) 0%, rgba(150,110,10,0.1) 100%)', border: '1px solid rgba(234,179,8,0.3)' }}>
        <div className="flex items-center gap-3 mb-3">
          <Sigil icon={Flame} color="#eab308" size={42} active />
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: 'rgba(234,179,8,0.75)', fontFamily: "'Courier New', monospace" }}>Casual · Fair · Simple</p>
            <h5 className="text-lg font-bold" style={{ color: 'rgba(240,230,255,0.95)', fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}>The Fair Launch</h5>
          </div>
        </div>
        <p className="text-[12.5px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
          <strong style={{ color: '#eab308' }}>$GFG</strong> launches on <strong>Pump.fun</strong> — bonded curve, anyone can buy from block one. No presale, no team wallets, no locked allocations. Just a meme coin.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          ['Pump.fun', 'Launch venue', '#eab308'],
          ['60 / 40', 'Dev / Community', '#ef4444'],
          ['SOL', 'Paid, not tokens', '#22c55e'],
        ].map(([v, l, c]) => (
          <div key={l} className="text-center p-3" style={{ background: `${c}06`, border: `1px solid ${c}25` }}>
            <p className="text-base font-black font-mono" style={{ color: c, textShadow: `0 0 6px ${c}50` }}>{v}</p>
            <p className="text-[9px] font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>{l}</p>
          </div>
        ))}
      </div>
      <p className="text-[11.5px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.6)' }}>
        Pump.fun pays <em>creator rewards</em> in SOL on every trade — straight to the public dev wallet. Each month <strong style={{ color: '#eab308' }}>40% of that SOL</strong> is paid out to top Descent + arcade players (real SOL, receipted on-chain). The other 60% covers hosting, Claude, RPCs and continued dev — no lockups, no promises beyond what shows up on the wallet.
      </p>
      <p className="text-[11px]" style={{ color: 'rgba(180,180,180,0.5)' }}>
        See the live vault balance + payout ledger on the{' '}
        <button onClick={() => navigate('/fair-launch')} className="underline" style={{ color: '#eab308' }}>Fair Launch page</button>.
      </p>
    </div>
  );

  if (chapterId === 'compliance') return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
        Every game in the arcade is a <strong style={{ color: '#ffd700' }}>skill based challenge</strong> with predetermined rewards. There are no bets, no jackpots, no cash outs. In game credits have zero monetary value and can never exchange for tokens.
      </p>
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
        The Burn Portal, where real $GFG is destroyed, is completely separate from any game. You burn tokens voluntarily to earn Season Points, not as part of gameplay. No staking, no prediction markets, no binary outcome bets exist anywhere.
      </p>
    </div>
  );

  if (chapterId === 'truth') return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
        $GFG is a meme coin. Like all meme coins, it carries risk. We cannot guarantee the token price will go up and will never insult your intelligence by pretending otherwise.
      </p>
      <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.7)' }}>
        What we can guarantee: the games are free, the arcade is fun, the burns are real, the code is open source. The Seasons system creates genuine reasons to participate beyond speculation, but the choice to buy tokens is always yours.
      </p>
      <p className="text-[13px] pt-2" style={{ color: 'rgba(255,215,0,0.85)', fontFamily: "'Cinzel', serif" }}>
        Never invest more than you can afford to lose. Do your own research. Check the{' '}
        <button onClick={() => navigate('/fair-launch')} className="underline" style={{ color: '#c084fc' }}>fair-launch page</button> for the live creator-rewards wallet + payout receipts.
      </p>
    </div>
  );

  return null;
};

// ───────── Main AboutPage ─────────
export default function AboutPage() {
  const navigate = useNavigate();
  const [activeChapter, setActiveChapter] = useState('problem');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const active = useMemo(() => CHAPTERS.find(c => c.id === activeChapter) || CHAPTERS[0], [activeChapter]);
  const ActiveIcon = active.icon;

  return (
    <div className="min-h-screen relative" style={{
      background: 'radial-gradient(ellipse at top, rgba(30,10,40,0.4) 0%, rgba(4,2,8,0.98) 60%, rgba(0,0,2,1) 100%)',
      color: 'rgba(240,230,255,0.9)',
    }}>
      {/* Ambient background — desktop only for perf */}
      {!isMobile && (
        <div className="fixed inset-0 z-0 pointer-events-none">
          <motion.div className="absolute top-[10%] left-[25%] w-[420px] h-[420px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.12), transparent 70%)', filter: 'blur(60px)' }}
            animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} />
          <motion.div className="absolute bottom-[20%] right-[15%] w-[380px] h-[380px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,107,0,0.08), transparent 70%)', filter: 'blur(60px)' }}
            animate={{ opacity: [0.3, 0.55, 0.3], scale: [1.1, 1, 1.1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }} />
        </div>
      )}

      <div className="relative z-10 pt-6 px-4 sm:px-6">
        <BackButton to="/" fixed={false} />
      </div>

      <section className="px-4 sm:px-6 pb-20 relative z-10 overflow-x-hidden">
        <div className="max-w-5xl mx-auto w-full min-w-0">
          {/* ═══ HERO ═══ */}
          <Reveal>
            <div className="text-center pt-8 md:pt-14 pb-10 md:pb-16">
              {/* Sigil */}
              <div className="flex justify-center mb-5 md:mb-6">
                <Sigil icon={Scroll} color="#a855f7" size={isMobile ? 54 : 68} active />
              </div>
              {/* Label */}
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)' }}>
                <div className="w-1 h-1 rounded-full" style={{ background: '#a855f7', boxShadow: '0 0 4px #a855f7' }} />
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#c084fc' }}>The Grimoire of $GFG</span>
              </div>
              {/* Title */}
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-3" data-testid="about-page-title"
                style={{
                  fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                  background: 'linear-gradient(180deg, #fbbf24 0%, #a855f7 50%, #4a1d7a 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 18px rgba(168,85,247,0.35))',
                  lineHeight: 1.1,
                }}>
                How $GFG Works
              </h1>
              {/* Flourish */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.5))' }} />
                <div className="w-1.5 h-1.5 rotate-45" style={{ background: '#a855f7' }} />
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(270deg, transparent, rgba(168,85,247,0.5))' }} />
              </div>
              <p className="text-[13px] md:text-sm max-w-xl mx-auto px-4" style={{ color: 'rgba(220,210,240,0.6)', fontFamily: "'Cinzel', serif" }}>
                Twelve chapters. No hype. No empty promises. Just the mechanics laid bare.
              </p>
            </div>
          </Reveal>

          {/* ═══ STATS STRIP ═══ */}
          <Reveal delay={0.1}>
            <div className="grid grid-cols-4 gap-2 md:gap-3 mb-10 md:mb-14">
              {[
                { v: '30', l: 'DAY SEASONS', c: '#22c55e' },
                { v: '4', l: 'ARCADE GAMES', c: '#a855f7' },
                { v: '92%', l: 'COMMUNITY', c: '#ffd700' },
                { v: '2%', l: 'TITHE BURN', c: '#ff6b00' },
              ].map(s => (
                <div key={s.l} className="p-2.5 md:p-4 text-center relative overflow-hidden"
                  style={{ background: 'rgba(10,5,15,0.5)', border: `1px solid ${s.c}25` }}>
                  <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: `linear-gradient(90deg, transparent, ${s.c}, transparent)` }} />
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold leading-none mb-1"
                    style={{ color: s.c, fontFamily: "'Impact', sans-serif", textShadow: `0 0 10px ${s.c}50` }}>{s.v}</p>
                  <p className="text-[8px] md:text-[9px] tracking-[0.15em] md:tracking-[0.2em] uppercase font-bold"
                    style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.4)' }}>{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* ═══ INTERACTIVE CHAPTER BROWSER ═══ */}
          <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-4 md:gap-6 mb-12 min-w-0">
            {/* Chapter sidebar */}
            <Reveal delay={0.15} className="min-w-0">
              <div className="relative min-w-0">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <Compass className="w-3.5 h-3.5" style={{ color: 'rgba(168,85,247,0.6)' }} />
                  <span className="text-[9px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(168,85,247,0.6)' }}>Table of Contents</span>
                </div>
                <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0 scrollbar-hide" style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
                  {CHAPTERS.map(ch => {
                    const isActive = ch.id === activeChapter;
                    return (
                      <button
                        key={ch.id}
                        onClick={() => setActiveChapter(ch.id)}
                        data-testid={`chapter-${ch.id}`}
                        className="flex-shrink-0 lg:flex-shrink text-left p-2.5 md:p-3 transition-all group min-w-[180px] lg:min-w-0"
                        style={{
                          background: isActive ? `${ch.color}10` : 'rgba(10,5,15,0.4)',
                          border: `1px solid ${isActive ? ch.color + '60' : 'rgba(255,255,255,0.04)'}`,
                          boxShadow: isActive ? `0 0 18px ${ch.color}20, inset 0 0 10px ${ch.color}08` : 'none',
                          scrollSnapAlign: 'start',
                        }}
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="text-[9px] font-mono font-bold w-6 text-center flex-shrink-0" style={{ color: isActive ? ch.color : 'rgba(255,255,255,0.3)' }}>
                            {ch.num}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-bold truncate" style={{ color: isActive ? 'rgba(240,230,255,0.95)' : 'rgba(240,230,255,0.7)', fontFamily: "'Cinzel', serif" }}>
                              {ch.title}
                            </p>
                            <p className="text-[10px] truncate hidden lg:block" style={{ color: 'rgba(255,255,255,0.35)' }}>
                              {ch.sub}
                            </p>
                          </div>
                          <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all hidden lg:block" style={{ color: ch.color }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            {/* Active chapter content */}
            <div className="relative min-h-[380px] min-w-0 w-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(180deg, rgba(15,8,20,0.6), rgba(8,4,12,0.8))',
                    border: `1px solid ${active.color}30`,
                    boxShadow: `0 0 40px ${active.color}08, inset 0 0 40px ${active.color}05`,
                  }}
                  data-testid="active-chapter-panel"
                >
                  {/* Corner brackets */}
                  <div className="absolute top-2 left-2 w-3 h-3 border-t border-l pointer-events-none" style={{ borderColor: `${active.color}60` }} />
                  <div className="absolute top-2 right-2 w-3 h-3 border-t border-r pointer-events-none" style={{ borderColor: `${active.color}60` }} />
                  <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l pointer-events-none" style={{ borderColor: `${active.color}60` }} />
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r pointer-events-none" style={{ borderColor: `${active.color}60` }} />

                  {/* Top strip accent */}
                  <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${active.color}80, transparent)` }} />

                  <div className="p-5 md:p-7">
                    {/* Chapter header */}
                    <div className="flex items-start gap-4 mb-5 md:mb-6 pb-4 md:pb-5" style={{ borderBottom: `1px solid ${active.color}15` }}>
                      <Sigil icon={ActiveIcon} color={active.color} size={isMobile ? 44 : 52} active />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5" style={{ background: `${active.color}18`, color: active.color }}>
                            CHAPTER {active.num}
                          </span>
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold mb-1"
                          style={{
                            fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                            background: `linear-gradient(180deg, ${active.color} 0%, ${active.color}90 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}>
                          {active.title}
                        </h3>
                        <p className="text-[11.5px] italic" style={{ color: 'rgba(220,210,240,0.5)' }}>{active.sub}</p>
                      </div>
                    </div>

                    <ChapterContent chapterId={active.id} navigate={navigate} />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* ═══ CTAs ═══ */}
          <Reveal delay={0.1}>
            <div className="flex flex-col sm:flex-row justify-center gap-3 pb-6">
              {[
                { icon: Coins, label: 'Fair Launch', path: '/fair-launch', color: '#eab308', testid: 'about-fair-launch-btn' },
                { icon: BookOpen, label: 'Documentation', path: '/docs', color: '#a855f7', testid: 'about-docs-btn' },
                { icon: BookOpen, label: 'Read the Lore', path: '/lore', color: '#22c55e', testid: 'about-lore-btn' },
              ].map(cta => (
                <motion.button key={cta.path}
                  onClick={() => navigate(cta.path)}
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={cta.testid}
                  style={{ background: `${cta.color}06`, border: `1px solid ${cta.color}30`, boxShadow: `inset 0 0 10px ${cta.color}05` }}>
                  <cta.icon className="w-4 h-4" style={{ color: cta.color }} />
                  <span className="text-sm font-bold tracking-wider" style={{ color: cta.color, fontFamily: "'Cinzel', serif" }}>{cta.label}</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" style={{ color: cta.color }} />
                </motion.button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
